"use client";

import { useEffect, useRef } from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { useAuthStore } from "@/store/auth.store";
import { initPostHog } from "@/services/analytics/posthog";
import { initAmplitude } from "@/services/analytics/amplitude";
import { GA_MEASUREMENT_ID, GA_ENABLED } from "@/services/analytics/google";
import {
  FB_PIXEL_ID,
  FB_ENABLED,
  flushFacebookUserData,
} from "@/services/analytics/facebook";
import { analytics } from "@/services/analytics";

/**
 * Initialise les SDKs analytics (PostHog + Amplitude + GA4 + Meta Pixel) et branche le
 * tracking automatique : pageviews sur changement de route + identify/reset sur
 * l'état d'authentification. Monté une fois dans les providers globaux.
 *
 * Les effets (init, identify, pageview) tournent indépendamment des scripts
 * tiers : PostHog fonctionne même sans `NEXT_PUBLIC_GA_MEASUREMENT_ID` ni
 * `NEXT_PUBLIC_FB_PIXEL_ID`, et chaque script ne se charge que si son ID est
 * configuré.
 */
export function AnalyticsProvider() {
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);
  const identifiedKey = useRef<string | null>(null);
  const firstPageview = useRef(true);

  // Init des SDK npm une fois, côté client. Les scripts tiers (gtag, Pixel)
  // sont chargés plus bas par next/script.
  //
  // Ce composant est monté AVANT `{children}` dans `providers/index.tsx` : son
  // effet part donc avant ceux des pages, qui peuvent émettre dès leur montage.
  // L'ordre compte — React exécute les effets des enfants avant ceux des
  // parents, mais respecte l'ordre entre frères.
  useEffect(() => {
    initPostHog();
    initAmplitude();
  }, []);

  // Identify au login, reset au logout — sans rejouer à chaque render.
  useEffect(() => {
    const key = user?.phone ?? user?.id ?? null;
    if (key) {
      if (identifiedKey.current !== key) {
        analytics.identifyUser({
          id: user?.id,
          phone: user?.phone,
          email: user?.email ?? undefined,
          name: user?.name,
        });
        identifiedKey.current = key;
      }
    } else if (identifiedKey.current) {
      analytics.logout();
      identifiedKey.current = null;
    }
  }, [user]);

  // Pageview sur changement de route. Le tout premier pageview est déjà émis
  // par les snippets tiers (`gtag('config', …)` pour GA, `fbq('track',
  // 'PageView')` pour le Pixel) : on n'envoie alors qu'à PostHog pour éviter un
  // double comptage sur la page d'entrée.
  useEffect(() => {
    if (!pathname) return;
    if (firstPageview.current) {
      firstPageview.current = false;
      analytics.trackPage(pathname, undefined, { to: ["posthog"] });
    } else {
      analytics.trackPage(pathname);
    }
  }, [pathname]);

  // Scripts tiers — chacun conditionné à son propre ID.
  return (
    <>
      {GA_ENABLED && (
        <>
          <Script
            id="ga4-lib"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              window.gtag = gtag;
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}');
            `}
          </Script>
        </>
      )}

      {/* Meta Pixel — snippet officiel : il définit `fbq` (avec sa file
          d'attente) puis charge fbevents.js, initialise le pixel et émet le
          PageView d'entrée. Les routes suivantes sont trackées par l'effet
          ci-dessus via `analytics.trackPage`. */}
      {FB_ENABLED && (
        <>
          <Script
            id="meta-pixel"
            strategy="afterInteractive"
            onReady={flushFacebookUserData}
          >
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${FB_PIXEL_ID}');
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              alt=""
              src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      )}
    </>
  );
}
